import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, GraduationCap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const getDomain = (url) => {
  if (!url) return null;
  return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
};

const getFaviconUrl = (schoolUrl, sz = 64) => {
  const domain = getDomain(schoolUrl);
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=${sz}` : null;
};

const CollegeLogo = ({ schoolUrl, name, size = 32 }) => {
  const [errored, setErrored] = useState(false);
  const faviconUrl = getFaviconUrl(schoolUrl, 128);

  if (!faviconUrl || errored) {
    return (
      <div
        style={{ width: size, height: size }}
        className="rounded bg-primary/10 flex items-center justify-center shrink-0"
      >
        <GraduationCap className="w-4 h-4 text-primary/60" />
      </div>
    );
  }

  return (
    <img
      src={faviconUrl}
      alt={name}
      style={{ width: size, height: size }}
      className="rounded object-contain shrink-0 bg-white border border-border"
      onError={() => setErrored(true)}
    />
  );
};

const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const CollegeSearchCombobox = ({ value, schoolUrl, onSelect }) => {
  const { api } = useAuth();
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value ? { name: value, schoolUrl } : null);
  const debouncedQuery = useDebounce(query, 300);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value !== (selected?.name || '')) {
      setSelected(value ? { name: value, schoolUrl } : null);
      setQuery(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2 || selected?.name === debouncedQuery) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/college-scorecard/search?q=${encodeURIComponent(debouncedQuery)}&per_page=8`)
      .then(res => {
        if (!cancelled) setResults(res.data.results || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (selected) setQuery(selected.name);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selected]);

  const handleSelect = useCallback((school) => {
    setSelected(school);
    setQuery(school.name);
    setOpen(false);
    setResults([]);
    onSelect(school);
  }, [onSelect]);

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    onSelect(null);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val !== selected?.name) {
      setSelected(null);
    }
    if (val.length >= 2) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const showDropdown = open && (loading || results.length > 0);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        {selected?.schoolUrl ? (
          <img
            src={getFaviconUrl(selected.schoolUrl, 128)}
            alt=""
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded object-contain"
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : null}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (query.length >= 2 && results.length > 0) setOpen(true); }}
          placeholder="Search colleges and universities…"
          className="w-full h-10 pl-9 pr-8 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 transition-colors"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
        {!loading && query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg overflow-hidden">
          {loading && results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="py-5 text-center text-sm text-muted-foreground">No results found</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto py-1">
              {results.map(school => (
                <li key={school.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(school)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 text-left transition-colors"
                  >
                    <CollegeLogo schoolUrl={school.schoolUrl} name={school.name} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{school.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                        <span>{[school.city, school.state].filter(Boolean).join(', ')}</span>
                        {school.ownershipLabel && (
                          <>
                            <span className="text-border">·</span>
                            <span>{school.ownershipLabel}</span>
                          </>
                        )}
                        {school.enrollmentSize != null && (
                          <>
                            <span className="text-border">·</span>
                            <span>{Number(school.enrollmentSize).toLocaleString()} students</span>
                          </>
                        )}
                      </div>
                    </div>
                    {school.admissionRate != null && (
                      <div className="text-right shrink-0">
                        <div className="text-xs font-semibold text-foreground">
                          {(school.admissionRate * 100).toFixed(0)}%
                        </div>
                        <div className="text-[10px] text-muted-foreground">admit</div>
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export { CollegeLogo, getFaviconUrl, getDomain };
export default CollegeSearchCombobox;
